import React from "react";

import {
  Box,
  Card,
  CardHeader,
  Divider,
  Grid,
  Switch,
  Typography,
  colors,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { uniq } from "lodash";

import { LessonType, LessonTypePreference } from "@hubcapp/types";

import { LessonTypesProps } from "./types";
import CardSkeleton from "./components/Skeleton";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
  },
  title: {
    lineHeight: "1.6",
    color: colors.blueGrey[900],
  },
  subtitle: {
    lineHeight: "1.6",
    color: colors.blueGrey[600],
  },
}));

const LessonTypesCard = ({
  loading = false,
  lessonTypes = [],
  onChange = () => {},
}: LessonTypesProps) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  if (loading) {
    return <CardSkeleton />;
  }

  const onSwitchChange = (lessonTypePreference: LessonTypePreference) => {
    const updatedVals = lessonTypes.map(ltp => {
      if (ltp.lessonType === lessonTypePreference.lessonType) {
        ltp.enabled = !ltp.enabled;
      }
      return ltp;
    });
    onChange(uniq(updatedVals), lessonTypePreference);
  };

  const standardLessonTypePreference = lessonTypes.find(
    ltp => ltp.lessonType === LessonType.Standard,
  );
  const passPlusLessonTypePreference = lessonTypes.find(
    ltp => ltp.lessonType === LessonType.PassPlus,
  );
  const shortNoticeTestLessonTypePreference = lessonTypes.find(
    ltp => ltp.lessonType === LessonType.ShortNoticeTest,
  );
  const ditLessonTypePreference = lessonTypes.find(
    ltp => ltp.lessonType === LessonType.Dit,
  );
  const motabilityLessonTypePreference = lessonTypes.find(
    ltp => ltp.lessonType === LessonType.Motability,
  );

  return (
    <Card data-test="lesson-types">
      <CardHeader title="My lesson types" />
      <Divider />
      {standardLessonTypePreference && (
        <Box>
          <Grid container className={classes.container}>
            <Box flexDirection="column" flex={1}>
              <Typography variant="body2" className={classes.title}>
                Standard lessons
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                {isMobile
                  ? "Learner, Refresher & Motorway"
                  : "Learner, Refresher & Motorway lesson"}
              </Typography>
            </Box>
            <Box>
              <Switch
                data-test="standard-lesson-type"
                disabled
                checked={Boolean(standardLessonTypePreference?.enabled)}
              />
            </Box>
          </Grid>
        </Box>
      )}
      {passPlusLessonTypePreference && (
        <Box>
          <Grid container className={classes.container}>
            <Box flexDirection="column" flex={1}>
              <Typography variant="body2" className={classes.title}>
                Pass Plus lessons
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                Qualified instructors only
              </Typography>
            </Box>
            <Box>
              <Switch
                data-test="pass-plus-lesson-type"
                onChange={() => onSwitchChange(passPlusLessonTypePreference)}
                checked={passPlusLessonTypePreference.enabled}
              />
            </Box>
          </Grid>
        </Box>
      )}
      {shortNoticeTestLessonTypePreference && (
        <Box>
          <Grid container className={classes.container}>
            <Box flexDirection="column" flex={1}>
              <Typography variant="body2" className={classes.title}>
                Short notice tests
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                Test is already booked within 14 days
              </Typography>
            </Box>
            <Box>
              <Switch
                data-test="short-notice-test-lesson-type"
                onChange={() =>
                  onSwitchChange(shortNoticeTestLessonTypePreference)
                }
                checked={shortNoticeTestLessonTypePreference.enabled}
              />
            </Box>
          </Grid>
        </Box>
      )}
      {ditLessonTypePreference && (
        <Box>
          <Grid container className={classes.container}>
            <Box flexDirection="column" flex={1}>
              <Typography variant="body2" className={classes.title}>
                Driver instructor training
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                Train provisional instructors
              </Typography>
            </Box>
            <Box>
              <Switch
                data-test="dit-lesson-type"
                onChange={() => onSwitchChange(ditLessonTypePreference)}
                disabled
                checked={ditLessonTypePreference.enabled}
              />
            </Box>
          </Grid>
        </Box>
      )}
      {motabilityLessonTypePreference && (
        <Box>
          <Grid container className={classes.container}>
            <Box flexDirection="column" flex={1}>
              <Typography variant="body2" className={classes.title}>
                Motability lessons
              </Typography>
              <Typography variant="body2" className={classes.subtitle}>
                Delivers Motability lessons
              </Typography>
            </Box>
            <Box>
              <Switch
                data-test="motability-lesson-type"
                onChange={() => onSwitchChange(motabilityLessonTypePreference)}
                disabled
                checked={motabilityLessonTypePreference.enabled}
              />
            </Box>
          </Grid>
        </Box>
      )}
    </Card>
  );
};

export default LessonTypesCard;
